import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';
import { XMarkIcon } from '@heroicons/react/24/outline';
import NotificationList from './NotificationList';
import NotificationActions from './NotificationActions';

// GraphQL mutation for bulk marking notifications as read
const MARK_NOTIFICATIONS_AS_READ = gql`
  mutation MarkNotificationsAsRead($ids: [ID!]!) {
    readNotifications(ids: $ids)
  }
`;

// GraphQL mutation for bulk deleting notifications
const DELETE_NOTIFICATIONS = gql`
  mutation DeleteNotifications($ids: [ID!]!) {
    deleteNotifications(ids: $ids)
  }
`;

const NotificationDrawer = ({
  isOpen,
  onClose,
  notifications,
  openCount,
  updateCount,
  after,
  hasNextPage,
  fetchMore,
  resetLoaded,
  loading,
  error
}) => {
  const intl = useIntl();
  const drawerRef = useRef(null);
  
  // GraphQL mutations
  const [markNotificationsAsRead] = useMutation(MARK_NOTIFICATIONS_AS_READ);
  const [deleteNotifications] = useMutation(DELETE_NOTIFICATIONS);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      // Small delay to prevent immediate closing
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Focus management
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [isOpen]);

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.readAt);
    if (unreadNotifications.length > 0) {
      try {
        const notificationIds = unreadNotifications.map(n => n.id);
        await markNotificationsAsRead({
          variables: { ids: notificationIds }
        });
        
        // Update local state to reflect read status
        const updatedNotifications = notifications.map(n => 
          unreadNotifications.find(un => un.id === n.id) 
            ? { ...n, readAt: new Date().toISOString() }
            : n
        );
        
        resetLoaded({
          notifications: updatedNotifications,
          after,
          hasNextPage
        });
        
        // Update count
        updateCount(unreadNotifications.length);
        
        console.log('Marked all notifications as read');
      } catch (error) {
        console.error('Failed to mark notifications as read:', error);
      }
    }
  };

  // Handle clear all notifications - now actually deletes them from the database
  const handleClearAll = async () => {
    if (notifications.length > 0) {
      try {
        // Delete all notifications from the database
        const allNotificationIds = notifications.map(n => n.id);
        console.log('🗑️ About to delete notifications with IDs:', allNotificationIds);
        
        const result = await deleteNotifications({
          variables: { ids: allNotificationIds }
        });
        
        console.log('🗑️ Delete mutation result:', result);
        
        // Clear from local state
        resetLoaded({
          notifications: [],
          after: null,
          hasNextPage: false
        });
        
        // Reset count to 0
        updateCount(openCount);
        
        console.log('🗑️ Deleted all notifications from database - local state cleared');
      } catch (error) {
        console.error('🗑️ Failed to delete notifications:', error);
        console.error('🗑️ Error details:', error.graphQLErrors, error.networkError);
      }
    }
  };

  if (!isOpen) return null;

  // Use portal to render at document root level to avoid z-index stacking context issues
  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        style={{ zIndex: 2147483646 }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        tabIndex={-1}
        className="fixed top-0 right-0 w-96 h-full bg-succession-bg-primary shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out focus:outline-none"
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          zIndex: 2147483647,
        }}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-succession-border bg-succession-bg-secondary">
          <div className="flex items-center justify-between w-full">
            <h2 className="text-xl font-semibold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.notifications.title' })}
              {openCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-succession-primary-10 text-succession-primary">
                  {intl.formatMessage({ id: 'succession.notifications.unread' }, { count: openCount })}
                </span>
              )}
            </h2>

            <div className="flex items-center space-x-2">
              <NotificationActions
                unreadCount={openCount}
                totalCount={notifications.length}
                onMarkAllAsRead={handleMarkAllAsRead}
                onClearAll={handleClearAll}
                isLoading={loading}
              />
              
              <button
                onClick={onClose}
                className="p-2 text-succession-text-secondary hover:text-succession-text-primary transition-colors duration-200 rounded-lg hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                aria-label={intl.formatMessage({ id: 'succession.notifications.close' })}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content - Make this scrollable */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {error ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-succession-error-bg rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-succession-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.notifications.failed_to_load' })}</h3>
                <p className="text-sm text-succession-text-secondary mb-4">{error.message || intl.formatMessage({ id: 'succession.notifications.something_went_wrong' })}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-succession-error text-white rounded-lg hover:bg-succession-error/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                >
                  {intl.formatMessage({ id: 'succession.notifications.retry' })}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <NotificationList
                notifications={notifications}
                openCount={openCount}
                updateCount={updateCount}
                after={after}
                hasNextPage={hasNextPage}
                fetchMore={fetchMore}
                resetLoaded={resetLoaded}
                loading={loading}
                error={error}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t border-succession-border bg-succession-bg-secondary">
          <div className="text-xs text-succession-text-muted text-center">
            {notifications.length > 0 ? (
              intl.formatMessage({ id: 'succession.notifications.showing' }, { count: notifications.length })
            ) : (
              intl.formatMessage({ id: 'succession.notifications.no_notifications' })
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

NotificationDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notifications: PropTypes.array,
  openCount: PropTypes.number,
  updateCount: PropTypes.func,
  after: PropTypes.string,
  hasNextPage: PropTypes.bool,
  fetchMore: PropTypes.func,
  resetLoaded: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.object,
};

NotificationDrawer.defaultProps = {
  notifications: [],
  openCount: 0,
  updateCount: () => {},
  after: null,
  hasNextPage: false,
  fetchMore: () => {},
  resetLoaded: () => {},
  loading: false,
  error: null,
};

export default NotificationDrawer; 